import { getSampleChipIds } from '@resistapp/common/utils';
import { chain } from 'lodash';
import { encodeReproState, REPRO_STATE_PARAM, type ReproUrlState } from '../reproducibility/reproducibility-url-state';

interface ChipSampleCell {
  chipId: number;
  sampleId: number;
}

interface ReproLinkChip {
  id: number;
  name: string;
}

interface ReproLinkSample {
  id: number;
  chipIds?: number[];
  abundances?: Parameters<typeof getSampleChipIds>[0][number]['abundances'];
  cts?: Parameters<typeof getSampleChipIds>[0][number]['cts'];
}

type ReproLinkSamplesByUID = Record<string, ReproLinkSample[]>;

export function buildReproducibilityHref(state: ReproUrlState | null): string {
  if (!state) return '/reproducibility';
  return `/reproducibility?${REPRO_STATE_PARAM}=${encodeURIComponent(encodeReproState(state))}`;
}

export function buildSelectedIdenticalSamplesReproState(
  samplesByUID: ReproLinkSamplesByUID | undefined,
  chips: ReproLinkChip[],
): ReproUrlState | null {
  const chipNameById = new Map(chips.map(chip => [chip.id, chip.name] as const));
  const usableGroups = Object.values(samplesByUID ?? {})
    .map(getUnambiguousChipSampleCells)
    .filter(cells => cells.length >= 2);

  if (usableGroups.length < 2 || !formsSingleConnectedChipGraph(usableGroups)) {
    return null;
  }

  const chipIds = chain(usableGroups)
    .flatMap(cells => cells.map(cell => cell.chipId))
    .uniq()
    .sortBy(chipId => chipId)
    .value();

  if (chipIds.length < 2) {
    return null;
  }

  const groups = usableGroups.map(cells => {
    const sampleIdByChipId = new Map(cells.map(cell => [cell.chipId, cell.sampleId] as const));
    return chipIds.map(chipId => sampleIdByChipId.get(chipId) ?? null);
  });

  return {
    mode: 'sets',
    setLabels: chipIds.map(chipId => formatChipLabel(chipId, chipNameById)),
    chipIds,
    assayScope: 'shared',
    groups,
  };
}

function getUnambiguousChipSampleCells(samples: ReproLinkSample[]): ChipSampleCell[] {
  const cells = samples.flatMap(sample => getSampleChipIds([sample]).map(chipId => ({ chipId, sampleId: sample.id })));

  return chain(cells)
    .groupBy(cell => cell.chipId)
    .entries()
    .map(([chipIdText, chipCells]) => {
      const sampleIds = chain(chipCells)
        .map(cell => cell.sampleId)
        .uniq()
        .sortBy(sampleId => sampleId)
        .value();
      return sampleIds.length === 1 ? { chipId: Number(chipIdText), sampleId: sampleIds[0] } : null;
    })
    .compact()
    .sortBy(cell => cell.chipId)
    .value();
}

function formsSingleConnectedChipGraph(groups: ChipSampleCell[][]): boolean {
  const chipIds = chain(groups)
    .flatMap(cells => cells.map(cell => cell.chipId))
    .uniq()
    .value();
  if (chipIds.length === 0) return false;

  const connectedChipIds = expandConnectedChipIds(new Set([chipIds[0]]), groups);
  return chipIds.every(chipId => connectedChipIds.has(chipId));
}

function expandConnectedChipIds(connectedChipIds: Set<number>, groups: ChipSampleCell[][]): Set<number> {
  const nextChipIds = chain(groups)
    .filter(cells => cells.some(cell => connectedChipIds.has(cell.chipId)))
    .flatMap(cells => cells.map(cell => cell.chipId))
    .uniq()
    .value();

  return nextChipIds.some(chipId => !connectedChipIds.has(chipId))
    ? expandConnectedChipIds(new Set([...connectedChipIds, ...nextChipIds]), groups)
    : connectedChipIds;
}

function formatChipLabel(chipId: number, chipNameById: Map<number, string>): string {
  const name = chipNameById.get(chipId);
  return name ? `${name} (#${chipId})` : `Chip #${chipId}`;
}
